import { Divider } from "@material-ui/core";
import type { ReactNode, Ref } from "react";
import React, { forwardRef, useCallback } from "react";
import { MenuItemButton } from "~/primitiveComponents/navigation/MenuItems/MenuItemButton/MenuItemButton";
import { MenuItemExternalLink } from "~/primitiveComponents/navigation/MenuItems/MenuItemExternalLink/MenuItemExternalLink";
import { MenuItemInformation } from "~/primitiveComponents/navigation/MenuItems/MenuItemInformation/MenuItemInformation";
import type { LinkSize, ShowLinkAsActive } from "~/primitiveComponents/navigation/MenuItems/MenuItemInternalLink/MenuItemInternalLink";
import { MenuItemInternalLink } from "~/primitiveComponents/navigation/MenuItems/MenuItemInternalLink/MenuItemInternalLink";
import { MenuItemToggle } from "~/primitiveComponents/navigation/MenuItems/MenuItemToggle/MenuItemToggle";
import { NestedMenu } from "~/primitiveComponents/navigation/MenuItems/NestedMenu/NestedMenu";
import { exhaustiveCheck } from "~/utils/exhaustiveCheck";
import { noOp } from "~/utils/noOp";

export interface ButtonMenuItem {
    type: "button";
    label: string;
    onClick: () => void;
}

export interface InternalLinkMenuItem {
    type: "internal-link";
    label: string;
    path: string;
    showAsActive?: ShowLinkAsActive; // Defaults to "never"
    onClick?: () => void;
}

export interface ExternalLinkMenuItem {
    type: "external-link";
    label: string;
    href: string;
}

export interface ToggleMenuItem {
    type: "toggle";
    label: string;
    isEnabled: boolean;
    setIsEnabled: (isEnabled: boolean) => void;
}

export interface InformationMenuItem {
    type: "information";
    key?: string;
    content: ReactNode;
}

export interface NestedMenuItems {
    type: "nested-menu";
    label: string;
    children: SimpleMenuItem[];
}

export const dividerMenuItem = { type: "divider" } as const;
export type MenuItemDivider = typeof dividerMenuItem;

export type SimpleMenuItem = ButtonMenuItem | InternalLinkMenuItem | ExternalLinkMenuItem | NestedMenuItems | ToggleMenuItem | InformationMenuItem | MenuItemDivider;

interface SimpleMenuItemsProps {
    items: SimpleMenuItem[];
    compact?: boolean;
    onClose?: () => void;
    autoFocus?: boolean;
}

// The forwardRef here is not important, but needed to prevent console errors that would otherwise say a ref hasn't been appropriately forwarded.
// This requirement has been removed in later versions of material-ui (v5 onwards).
// See https://github.com/mui-org/material-ui/pull/25691/files#diff-ad4b8459eb1cd3c5e6882eb705e3e341e551bd7bf4ab9a6941ef12017eb1cb06L177-L188
export const SimpleMenuItems = forwardRef(({ autoFocus, onClose, items, compact = false }: SimpleMenuItemsProps, ref: Ref<unknown>) => (
    <>
        {items.map((item, index) => {
            const key = item.type === "information" ? item.key ?? index : item.type === "divider" ? index : item.label;
            return <SimpleMenuItemComponent key={key} item={item} onClose={onClose ?? noOp} autoFocus={index === 0 && Boolean(autoFocus)} compact={compact} />;
        })}
    </>
));

interface SimpleMenuItemProps {
    item: SimpleMenuItem;
    onClose: () => void;
    autoFocus: boolean;
    compact: boolean;
}

function SimpleMenuItemComponent({ item, autoFocus, onClose, compact }: SimpleMenuItemProps) {
    switch (item.type) {
        case "button":
            return <SimpleMenuItemButton item={item} onClose={onClose} autoFocus={autoFocus} compact={compact} />;
        case "internal-link":
            return <SimpleMenuItemInternalLink item={item} onClose={onClose} autoFocus={autoFocus} size={compact ? "compact" : "default"} />;
        case "external-link":
            return <SimpleMenuItemExternalLink item={item} onClose={onClose} autoFocus={autoFocus} compact={compact} />;
        case "nested-menu":
            return <NestedMenuItemsComponent item={item} onClose={onClose} autoFocus={autoFocus} compact={compact} />;
        case "toggle":
            return <ToggleMenuItemComponent item={item} autoFocus={autoFocus} compact={compact} />;
        case "information":
            return <InformationMenuItemComponent item={item} compact={compact} />;
        case "divider":
            return <Divider />;
        default:
            exhaustiveCheck(item, "Unexpected simple menu item type");
    }
}

interface SimpleMenuItemButtonProps {
    item: ButtonMenuItem;
    onClose: () => void;
    autoFocus: boolean;
    compact: boolean;
}

function SimpleMenuItemButton({ item, onClose, autoFocus, compact }: SimpleMenuItemButtonProps) {
    const itemOnClick = item.onClick;
    const onClick = useCallback(() => {
        itemOnClick();
        onClose();
    }, [itemOnClick, onClose]);
    return (
        <MenuItemButton onClick={onClick} autoFocus={autoFocus} compact={compact}>
            {item.label}
        </MenuItemButton>
    );
}

interface SimpleMenuItemInternalLinkProps {
    item: InternalLinkMenuItem;
    onClose: () => void;
    autoFocus: boolean;
    size: LinkSize;
}

function SimpleMenuItemInternalLink({ item, onClose, autoFocus, size }: SimpleMenuItemInternalLinkProps) {
    const itemOnClick = item.onClick;
    const onClick = useCallback(() => {
        itemOnClick?.();
        onClose();
    }, [itemOnClick, onClose]);

    return <MenuItemInternalLink onClick={onClick} autoFocus={autoFocus} label={item.label} showLinkAsActive={item.showAsActive} path={item.path} size={size} />;
}

interface SimpleMenuItemExternalLinkProps {
    item: ExternalLinkMenuItem;
    onClose: () => void;
    autoFocus: boolean;
    compact: boolean;
}

function SimpleMenuItemExternalLink({ item, onClose, autoFocus, compact }: SimpleMenuItemExternalLinkProps) {
    return <MenuItemExternalLink href={item.href} label={item.label} onClick={onClose} autoFocus={autoFocus} compact={compact} />;
}

interface NestedMenuItemsProps {
    item: NestedMenuItems;
    onClose: () => void;
    autoFocus: boolean;
    compact: boolean;
}

function NestedMenuItemsComponent({ item, onClose, autoFocus, compact }: NestedMenuItemsProps) {
    return <NestedMenu onClose={onClose} autoFocus={autoFocus} label={item.label} childMenuItems={item.children} compact={compact} />;
}

interface ToggleMenuItemProps {
    item: ToggleMenuItem;
    autoFocus: boolean;
    compact: boolean;
}

function ToggleMenuItemComponent({ item: { setIsEnabled, isEnabled, label }, autoFocus, compact }: ToggleMenuItemProps) {
    return <MenuItemToggle isEnabled={isEnabled} label={label} setIsEnabled={setIsEnabled} autoFocus={autoFocus} compact={compact} />;
}

interface InformationMenuItemProps {
    item: InformationMenuItem;
    compact: boolean;
}

function InformationMenuItemComponent({ item, compact }: InformationMenuItemProps) {
    return <MenuItemInformation compact={compact}>{item.content}</MenuItemInformation>;
}
